import { tokenizeWithSpaces } from './diffUtils'
import { cleanTextForComparison, tokenizeComparisonWords } from './comparisonTextCleaner'
import { removeDiacritics, normalizeLetters } from './arabicMorph'

export type ApparatusVariantKind =
  | 'insertion'
  | 'deletion'
  | 'grammatical_shift'
  | 'morph'
  | 'orthographic'
  | 'vocalization'
  | 'synonym'
  | 'lexical_substitution'
  | 'reordering'
  | 'block_substitution'

export interface ApparatusSpan {
  kind: ApparatusVariantKind
  leftText: string
  rightText: string
  note?: string
  leftStart?: number
  leftEnd?: number
  rightStart?: number
  rightEnd?: number
}

export interface ApparatusOverlayToken {
  kind: 'word' | 'space' | 'ghost'
  text: string
  variant?: ApparatusVariantKind
}

type TokenMatch = {
  leftIndex: number
  rightIndex: number
  kind: ApparatusVariantKind
  score: number
}

type GapAnalysis = {
  smallSpans: ApparatusSpan[]
  onlySmallOneSided: boolean
  hasLargeOneSidedTail: boolean
}

type ExactBlock = {
  a: number
  b: number
  size: number
}

type IndexedApparatusToken = {
  text: string
  normalized: string
  index: number
  content: boolean
}

type ContentAnchor = {
  normalized: string
  leftIndex: number
  rightIndex: number
}

type ReorderingCandidate = {
  leftStart: number
  leftEnd: number
  rightStart: number
  rightEnd: number
  contentLength: number
  crossingAnchors: number
  note: 'exact-phrase-move' | 'pair-reversal'
}

const ARABIC_PUNCTUATION = /[،؛؟!.,:;"'«»()[\]{}]/g
const STOPWORD_TOKENS = new Set([
  'يا', 'عن', 'في', 'من', 'الى', 'إلى', 'على', 'ثم', 'ثمّ', 'و', 'ف', 'قد', 'ما', 'كان', 'له', 'لها', 'هو', 'هي',
  'لا', 'لم', 'لن', 'ولا', 'فلا', 'وقد', 'فقد', 'انه', 'فانه', 'إنه', 'فإنه', 'الذي', 'التي'
])
const REORDERING_NON_CONTENT_TOKENS = new Set([
  'يا', 'عن', 'في', 'من', 'الى', 'الي', 'علي', 'ثم', 'و', 'ف', 'قد', 'ما', 'كان', 'له', 'لها', 'هو', 'هي',
  'لا', 'لم', 'لن', 'ولا', 'فلا', 'وقد', 'فقد', 'انه', 'فانه', 'الذي', 'التي', 'او', 'ان', 'فان',
  'بما', 'كما', 'لك', 'لهم', 'لهن', 'فيه', 'فيها', 'منه', 'منها'
])
const FUNCTIONAL_TOKENS = new Set([
  'اياي', 'اياك', 'اياكما', 'اياكم', 'اياكن', 'اياه', 'اياها', 'اياهما', 'اياهم', 'اياهن',
  'انا', 'انت', 'انتما', 'انتم', 'انتن', 'نحن'
])
const NEGATION_TOKENS = new Set(['لا', 'لم', 'لن', 'ما', 'ولا', 'فلا'])
const DERIVATIONAL_PREFIX_CHARS = new Set(['ي', 'ت', 'ا', 'ن', 'م'])
const SUFFIXES = ['هما', 'كما', 'هن', 'هم', 'كم', 'كن', 'ها', 'نا', 'ني', 'ك', 'ي', 'ه']
const STEM_ENDINGS = ['ات', 'ون', 'ين', 'ان', 'ة', 'ه', 'ي', 'ت']
const SYNONYM_STEM_PAIRS = new Set(['حدث|خبر', 'حدث|اخبر', 'خبر|اخبر'])
const SMALL_GAP_LIMIT = 2
const MIN_EXACT_ANCHOR = 3
const LONG_DIFF_TOKEN_THRESHOLD = 24
const NON_SHADDA_DIACRITICS = /[\u064B-\u0650\u0652\u0670\u06D6-\u06ED]/
const SHADDA = '\u0651'

const normalizeApparatusToken = (token: string): string => (
  normalizeLetters(removeDiacritics(token).replace(/\u0640/g, '')).replace(ARABIC_PUNCTUATION, '')
)

const stripPrefixes = (token: string): string => {
  let current = token
  while (current.length > 4 && (current.startsWith('و') || current.startsWith('ف'))) {
    current = current.slice(1)
  }
  if (current.length > 5 && (current.startsWith('بال') || current.startsWith('كال') || current.startsWith('لل'))) {
    current = current.slice(1)
  }
  if (current.startsWith('ال')) {
    current = current.slice(2)
  }
  return current
}

const stripSuffixes = (token: string): string => {
  let current = token
  let changed = true
  while (changed) {
    changed = false
    for (const suffix of SUFFIXES) {
      if (current.endsWith(suffix) && current.length - suffix.length >= 2) {
        current = current.slice(0, -suffix.length)
        changed = true
        break
      }
    }
  }
  return current
}

const getStem = (token: string): string => {
  let current = stripSuffixes(stripPrefixes(normalizeApparatusToken(token)))
  if (current.length >= 4 && DERIVATIONAL_PREFIX_CHARS.has(current[0])) {
    current = current.slice(1)
  }
  for (const ending of STEM_ENDINGS) {
    if (current.endsWith(ending) && current.length - ending.length >= 3) {
      return current.slice(0, -ending.length)
    }
  }
  return current
}

const getCoreStem = (token: string): string => {
  let current = stripSuffixes(stripPrefixes(normalizeApparatusToken(token)))
  while (current.length >= 4 && DERIVATIONAL_PREFIX_CHARS.has(current[0])) {
    current = current.slice(1)
  }
  for (const ending of STEM_ENDINGS) {
    if (current.endsWith(ending) && current.length - ending.length >= 3) {
      current = current.slice(0, -ending.length)
      break
    }
  }
  return current
}

const levenshtein = (left: string, right: string): number => {
  if (left === right) return 0
  if (!left) return right.length
  if (!right) return left.length
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index)
  for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
    const current = [leftIndex]
    for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
      const cost = left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1
      current[rightIndex] = Math.min(
        previous[rightIndex] + 1,
        current[rightIndex - 1] + 1,
        previous[rightIndex - 1] + cost
      )
    }
    for (let index = 0; index < current.length; index += 1) {
      previous[index] = current[index]
    }
  }
  return previous[right.length]
}

const isContentToken = (token: string): boolean => {
  const normalized = normalizeApparatusToken(token)
  return Boolean(normalized) && normalized.length > 1 && !STOPWORD_TOKENS.has(normalized) && !FUNCTIONAL_TOKENS.has(normalized)
}

const isReorderingContentToken = (token: string): boolean => {
  const normalized = normalizeApparatusToken(token)
  return Boolean(normalized) &&
    normalized.length > 1 &&
    !REORDERING_NON_CONTENT_TOKENS.has(normalized) &&
    !FUNCTIONAL_TOKENS.has(normalized)
}

const isDetachedPronounToken = (token: string): boolean => FUNCTIONAL_TOKENS.has(normalizeApparatusToken(token))

const isNegationToken = (token: string): boolean => NEGATION_TOKENS.has(normalizeApparatusToken(token))

const isAnToken = (token: string): boolean => normalizeApparatusToken(token) === 'ان'

const isMorphMatch = (left: string, right: string): boolean => {
  const leftNorm = normalizeApparatusToken(left)
  const rightNorm = normalizeApparatusToken(right)
  if (!leftNorm || !rightNorm || leftNorm === rightNorm) {
    return false
  }
  const leftStem = getStem(left)
  const rightStem = getStem(right)
  const leftReduced = leftNorm !== leftStem
  const rightReduced = rightNorm !== rightStem
  if (leftStem && rightStem && leftStem === rightStem && (leftReduced || rightReduced)) {
    return true
  }
  const leftCore = getCoreStem(left)
  const rightCore = getCoreStem(right)
  if (leftCore && rightCore && leftCore === rightCore && (leftReduced || rightReduced)) {
    return true
  }
  if (leftCore && rightCore && Math.min(leftCore.length, rightCore.length) >= 3 && (leftReduced || rightReduced)) {
    return levenshtein(leftCore, rightCore) <= 1
  }
  return false
}

const hasNonShaddaDiacritic = (token: string): boolean => NON_SHADDA_DIACRITICS.test(token)

const containsShadda = (token: string): boolean => token.includes(SHADDA)

const isVocalizationMatch = (left: string, right: string): boolean => {
  if (!left || !right || left === right) {
    return false
  }
  const leftNorm = normalizeApparatusToken(left)
  const rightNorm = normalizeApparatusToken(right)
  if (!leftNorm || leftNorm !== rightNorm) {
    return false
  }
  const leftStripped = removeDiacritics(left).replace(/\u0640/g, '')
  const rightStripped = removeDiacritics(right).replace(/\u0640/g, '')
  if (leftStripped === rightStripped) {
    if (containsShadda(left) !== containsShadda(right)) {
      return false
    }
    return hasNonShaddaDiacritic(left) || hasNonShaddaDiacritic(right)
  }
  return hasNonShaddaDiacritic(left) || hasNonShaddaDiacritic(right)
}

const grammaticalForms = (token: string): Set<string> => {
  const normalized = normalizeApparatusToken(token)
  if (!normalized) {
    return new Set()
  }
  const forms = new Set<string>([normalized])
  const pending = [normalized]
  while (pending.length > 0) {
    const current = pending.pop() || ''
    const derived = new Set<string>()
    if (current.length > 2 && (current.startsWith('و') || current.startsWith('ف'))) {
      derived.add(current.slice(1))
    }
    if (current.startsWith('لل') && current.length > 3) {
      derived.add(`ال${current.slice(2)}`)
    }
    if ((current.startsWith('بال') || current.startsWith('كال')) && current.length > 4) {
      derived.add(current.slice(1))
    }
    derived.forEach(candidate => {
      if (candidate && !forms.has(candidate)) {
        forms.add(candidate)
        pending.push(candidate)
      }
    })
  }
  return forms
}

const isGrammaticalShift = (left: string, right: string): boolean => {
  const leftNorm = normalizeApparatusToken(left)
  const rightNorm = normalizeApparatusToken(right)
  if (!leftNorm || !rightNorm || leftNorm === rightNorm) {
    return false
  }
  const leftForms = grammaticalForms(left)
  const rightForms = grammaticalForms(right)
  for (const form of Array.from(leftForms)) {
    if (rightForms.has(form) && (leftForms.size > 1 || rightForms.size > 1)) {
      return true
    }
  }
  return !isContentToken(left) && !isContentToken(right)
}

const similarityRatio = (left: string, right: string): number => {
  const maxLength = Math.max(left.length, right.length)
  if (maxLength === 0) {
    return 1
  }
  return 1 - (levenshtein(left, right) / maxLength)
}

const isOrthographicMatch = (left: string, right: string): boolean => {
  const leftNorm = normalizeApparatusToken(left)
  const rightNorm = normalizeApparatusToken(right)
  if (!leftNorm || !rightNorm || leftNorm === rightNorm) {
    return left !== right && leftNorm === rightNorm && !isVocalizationMatch(left, right)
  }
  if (isMorphMatch(left, right)) {
    return false
  }
  if (isGrammaticalShift(left, right)) {
    return false
  }
  if (Math.min(leftNorm.length, rightNorm.length) < 3) {
    return false
  }
  const distance = levenshtein(leftNorm, rightNorm)
  return distance <= 1 || similarityRatio(leftNorm, rightNorm) >= 0.82
}

const isSynonymMatch = (left: string, right: string): boolean => {
  const leftStem = getCoreStem(left)
  const rightStem = getCoreStem(right)
  if (!leftStem || !rightStem || leftStem === rightStem) {
    return false
  }
  const pair = [leftStem, rightStem].sort().join('|')
  if (!SYNONYM_STEM_PAIRS.has(pair)) {
    return false
  }
  const leftNorm = normalizeApparatusToken(left)
  const rightNorm = normalizeApparatusToken(right)
  const looksReportingForm = (normalized: string) => (
    normalized.endsWith('ني') ||
    normalized.startsWith('اخب') ||
    normalized.startsWith('فاخب') ||
    normalized.startsWith('حدث') ||
    normalized.startsWith('فحدث')
  )
  return looksReportingForm(leftNorm) && looksReportingForm(rightNorm)
}

const classifyTokenPair = (
  left: string,
  right: string,
  options?: { allowLexical?: boolean }
): ApparatusVariantKind | null => {
  const allowLexical = options?.allowLexical === true
  const leftNorm = normalizeApparatusToken(left)
  const rightNorm = normalizeApparatusToken(right)
  if (!left || !right || !leftNorm || !rightNorm) {
    return null
  }
  if (left !== right && leftNorm === rightNorm) {
    if (isVocalizationMatch(left, right)) return 'vocalization'
    if (isOrthographicMatch(left, right)) return 'orthographic'
    return null
  }
  if (leftNorm === rightNorm) {
    return null
  }
  if (isGrammaticalShift(left, right)) return 'grammatical_shift'
  if (isMorphMatch(left, right)) return 'morph'
  if (isOrthographicMatch(left, right)) return 'orthographic'
  if (isSynonymMatch(left, right)) return 'synonym'
  const bothContent = isContentToken(left) && isContentToken(right)
  const bothFunctional = !isContentToken(left) && !isContentToken(right)
  if (allowLexical && (bothContent || (bothFunctional && Math.min(leftNorm.length, rightNorm.length) >= 2))) {
    return 'lexical_substitution'
  }
  return null
}

const joinTokens = (tokens: string[]): string => tokens.filter(Boolean).join(' ').trim()

const tokenize = (text: string): string[] => tokenizeComparisonWords(text)

const buildIndexedTokens = (tokens: string[]): IndexedApparatusToken[] => (
  tokens.map((text, index) => ({
    text,
    normalized: normalizeApparatusToken(text),
    index,
    content: isReorderingContentToken(text)
  }))
)

const groupByNormalizedToken = (tokens: IndexedApparatusToken[]): Map<string, IndexedApparatusToken[]> => {
  const grouped = new Map<string, IndexedApparatusToken[]>()
  tokens.forEach(token => {
    if (!token.content || !token.normalized) {
      return
    }
    const existing = grouped.get(token.normalized) || []
    existing.push(token)
    grouped.set(token.normalized, existing)
  })
  return grouped
}

const buildUniqueContentAnchors = (
  leftIndexed: IndexedApparatusToken[],
  rightIndexed: IndexedApparatusToken[]
): ContentAnchor[] => {
  const leftGrouped = groupByNormalizedToken(leftIndexed)
  const rightGrouped = groupByNormalizedToken(rightIndexed)
  const anchors: ContentAnchor[] = []
  leftGrouped.forEach((leftTokens, normalized) => {
    const rightTokens = rightGrouped.get(normalized)
    if (!rightTokens || leftTokens.length !== 1 || rightTokens.length !== 1) {
      return
    }
    anchors.push({
      normalized,
      leftIndex: leftTokens[0].index,
      rightIndex: rightTokens[0].index
    })
  })
  return anchors
}

const isIndexInside = (index: number, start: number, end: number): boolean => index >= start && index < end

const intervalOverlaps = (leftStart: number, leftEnd: number, rightStart: number, rightEnd: number): boolean => (
  leftStart < rightEnd && rightStart < leftEnd
)

const countCrossingAnchors = (
  candidate: Pick<ReorderingCandidate, 'leftStart' | 'leftEnd' | 'rightStart' | 'rightEnd'>,
  anchors: ContentAnchor[]
): number => (
  anchors.filter(anchor => {
    if (
      isIndexInside(anchor.leftIndex, candidate.leftStart, candidate.leftEnd) ||
      isIndexInside(anchor.rightIndex, candidate.rightStart, candidate.rightEnd)
    ) {
      return false
    }
    const crossesFromLeft = anchor.leftIndex < candidate.leftStart && anchor.rightIndex >= candidate.rightEnd
    const crossesFromRight = anchor.leftIndex >= candidate.leftEnd && anchor.rightIndex < candidate.rightStart
    return crossesFromLeft || crossesFromRight
  }).length
)

const distanceFromInterval = (index: number, start: number, end: number): number => {
  if (index < start) {
    return start - index
  }
  if (index >= end) {
    return index - end + 1
  }
  return 0
}

const hasLocalCrossingAnchor = (
  candidate: Pick<ReorderingCandidate, 'leftStart' | 'leftEnd' | 'rightStart' | 'rightEnd'>,
  anchors: ContentAnchor[],
  maxDistance = 6
): boolean => (
  anchors.some(anchor => {
    if (
      isIndexInside(anchor.leftIndex, candidate.leftStart, candidate.leftEnd) ||
      isIndexInside(anchor.rightIndex, candidate.rightStart, candidate.rightEnd)
    ) {
      return false
    }
    const crossesFromLeft = anchor.leftIndex < candidate.leftStart && anchor.rightIndex >= candidate.rightEnd
    const crossesFromRight = anchor.leftIndex >= candidate.leftEnd && anchor.rightIndex < candidate.rightStart
    if (!crossesFromLeft && !crossesFromRight) {
      return false
    }
    return (
      distanceFromInterval(anchor.leftIndex, candidate.leftStart, candidate.leftEnd) <= maxDistance &&
      distanceFromInterval(anchor.rightIndex, candidate.rightStart, candidate.rightEnd) <= maxDistance
    )
  })
)

const buildContentNgramMap = (
  contentTokens: IndexedApparatusToken[],
  size: number
): Map<string, Array<{ contentStart: number; contentEnd: number; rawStart: number; rawEnd: number }>> => {
  const map = new Map<string, Array<{ contentStart: number; contentEnd: number; rawStart: number; rawEnd: number }>>()
  for (let index = 0; index <= contentTokens.length - size; index += 1) {
    const group = contentTokens.slice(index, index + size)
    const signature = group.map(token => token.normalized).join('\u0001')
    if (!signature) {
      continue
    }
    const existing = map.get(signature) || []
    existing.push({
      contentStart: index,
      contentEnd: index + size,
      rawStart: group[0].index,
      rawEnd: group[group.length - 1].index + 1
    })
    map.set(signature, existing)
  }
  return map
}

const findExactPhraseMoveCandidates = (
  leftIndexed: IndexedApparatusToken[],
  rightIndexed: IndexedApparatusToken[],
  anchors: ContentAnchor[]
): ReorderingCandidate[] => {
  const leftContent = leftIndexed.filter(token => token.content)
  const rightContent = rightIndexed.filter(token => token.content)
  const candidates: ReorderingCandidate[] = []
  const maxSize = Math.min(6, leftContent.length, rightContent.length)
  for (let size = maxSize; size >= 2; size -= 1) {
    const leftNgrams = buildContentNgramMap(leftContent, size)
    const rightNgrams = buildContentNgramMap(rightContent, size)
    leftNgrams.forEach((leftMatches, signature) => {
      const rightMatches = rightNgrams.get(signature)
      if (!rightMatches || leftMatches.length !== 1 || rightMatches.length !== 1) {
        return
      }
      const leftMatch = leftMatches[0]
      const rightMatch = rightMatches[0]
      const baseCandidate = {
        leftStart: leftMatch.rawStart,
        leftEnd: leftMatch.rawEnd,
        rightStart: rightMatch.rawStart,
        rightEnd: rightMatch.rawEnd
      }
      const crossingAnchors = countCrossingAnchors(baseCandidate, anchors)
      if (crossingAnchors === 0 || !hasLocalCrossingAnchor(baseCandidate, anchors)) {
        return
      }
      candidates.push({
        ...baseCandidate,
        contentLength: size,
        crossingAnchors,
        note: 'exact-phrase-move'
      })
    })
  }
  return candidates
}

const findPairReversalCandidates = (
  leftIndexed: IndexedApparatusToken[],
  rightIndexed: IndexedApparatusToken[],
  anchors: ContentAnchor[]
): ReorderingCandidate[] => {
  const leftContent = leftIndexed.filter(token => token.content)
  const rightByNormalized = groupByNormalizedToken(rightIndexed)
  const candidates: ReorderingCandidate[] = []
  const expandStartOverFunctionTokens = (tokens: IndexedApparatusToken[], start: number): number => {
    let nextStart = start
    while (nextStart > 0 && !tokens[nextStart - 1].content && tokens[nextStart - 1].normalized) {
      nextStart -= 1
    }
    return nextStart
  }
  const contentWindow = (tokens: IndexedApparatusToken[], start: number, end: number): string[] => (
    tokens.slice(start, end).filter(token => token.content).map(token => token.normalized)
  )
  for (let index = 0; index < leftContent.length - 1; index += 1) {
    const first = leftContent[index]
    const second = leftContent[index + 1]
    const rightFirst = rightByNormalized.get(first.normalized)
    const rightSecond = rightByNormalized.get(second.normalized)
    if (
      !rightFirst ||
      !rightSecond ||
      rightFirst.length !== 1 ||
      rightSecond.length !== 1 ||
      rightFirst[0].index <= rightSecond[0].index
    ) {
      continue
    }
    const leftStart = expandStartOverFunctionTokens(leftIndexed, first.index)
    const leftEnd = second.index + 1
    const rightStart = expandStartOverFunctionTokens(rightIndexed, rightSecond[0].index)
    const rightEnd = rightFirst[0].index + 1
    const rawWindowLimit = 6
    if (leftEnd - leftStart > rawWindowLimit || rightEnd - rightStart > rawWindowLimit) {
      continue
    }
    const leftContentWindow = contentWindow(leftIndexed, leftStart, leftEnd)
    const rightContentWindow = contentWindow(rightIndexed, rightStart, rightEnd)
    if (
      leftContentWindow.length !== 2 ||
      rightContentWindow.length !== 2 ||
      leftContentWindow[0] !== first.normalized ||
      leftContentWindow[1] !== second.normalized ||
      rightContentWindow[0] !== second.normalized ||
      rightContentWindow[1] !== first.normalized
    ) {
      continue
    }
    candidates.push({
      leftStart,
      leftEnd,
      rightStart,
      rightEnd,
      contentLength: 2,
      crossingAnchors: Math.max(1, countCrossingAnchors({ leftStart, leftEnd, rightStart, rightEnd }, anchors)),
      note: 'pair-reversal'
    })
  }
  return candidates
}

const selectReorderingCandidates = (candidates: ReorderingCandidate[]): ReorderingCandidate[] => {
  const selected: ReorderingCandidate[] = []
  const sorted = candidates.slice().sort((left, right) => {
    const leftScore = left.contentLength * 10 + left.crossingAnchors * 2 + (left.note === 'pair-reversal' ? 6 : 0)
    const rightScore = right.contentLength * 10 + right.crossingAnchors * 2 + (right.note === 'pair-reversal' ? 6 : 0)
    if (rightScore !== leftScore) return rightScore - leftScore
    if (left.leftStart !== right.leftStart) return left.leftStart - right.leftStart
    return left.rightStart - right.rightStart
  })
  sorted.forEach(candidate => {
    const overlapsExisting = selected.some(existing => (
      intervalOverlaps(candidate.leftStart, candidate.leftEnd, existing.leftStart, existing.leftEnd) ||
      intervalOverlaps(candidate.rightStart, candidate.rightEnd, existing.rightStart, existing.rightEnd)
    ))
    if (!overlapsExisting) {
      selected.push(candidate)
    }
  })
  return selected.sort((left, right) => left.leftStart - right.leftStart || left.rightStart - right.rightStart)
}

const buildReorderingSpans = (
  leftTokens: string[],
  rightTokens: string[]
): ApparatusSpan[] => {
  const leftIndexed = buildIndexedTokens(leftTokens)
  const rightIndexed = buildIndexedTokens(rightTokens)
  const anchors = buildUniqueContentAnchors(leftIndexed, rightIndexed)
  if (anchors.length < 2) {
    return []
  }
  const candidates = selectReorderingCandidates([
    ...findExactPhraseMoveCandidates(leftIndexed, rightIndexed, anchors),
    ...findPairReversalCandidates(leftIndexed, rightIndexed, anchors)
  ])
  return candidates.map(candidate => ({
    kind: 'reordering' as const,
    leftText: joinTokens(leftTokens.slice(candidate.leftStart, candidate.leftEnd)),
    rightText: joinTokens(rightTokens.slice(candidate.rightStart, candidate.rightEnd)),
    note: candidate.note,
    leftStart: candidate.leftStart,
    leftEnd: candidate.leftEnd,
    rightStart: candidate.rightStart,
    rightEnd: candidate.rightEnd
  })).filter(span => Boolean(span.leftText && span.rightText))
}

const appendReorderingSpans = (
  spans: ApparatusSpan[],
  leftTokens: string[],
  rightTokens: string[]
): ApparatusSpan[] => {
  const reorderingSpans = buildReorderingSpans(leftTokens, rightTokens)
  if (reorderingSpans.length === 0) {
    return spans
  }
  const existingKeys = new Set(spans.map(span => `${span.kind}|${span.leftStart}|${span.leftEnd}|${span.rightStart}|${span.rightEnd}`))
  const additions = reorderingSpans.filter(span => !existingKeys.has(`${span.kind}|${span.leftStart}|${span.leftEnd}|${span.rightStart}|${span.rightEnd}`))
  return additions.length ? [...spans, ...additions] : spans
}

const findLongestExactMatch = (
  leftTokens: string[],
  rightTokens: string[],
  leftStart: number,
  leftEnd: number,
  rightStart: number,
  rightEnd: number
): ExactBlock => {
  let bestLeft = leftStart
  let bestRight = rightStart
  let bestSize = 0
  let previous = new Array(rightEnd - rightStart + 1).fill(0)
  for (let leftIndex = leftStart; leftIndex < leftEnd; leftIndex += 1) {
    const current = new Array(rightEnd - rightStart + 1).fill(0)
    for (let rightIndex = rightStart; rightIndex < rightEnd; rightIndex += 1) {
      if (leftTokens[leftIndex] !== rightTokens[rightIndex]) {
        continue
      }
      const size = previous[rightIndex - rightStart] + 1
      current[rightIndex - rightStart + 1] = size
      if (size > bestSize) {
        bestSize = size
        bestLeft = leftIndex - size + 1
        bestRight = rightIndex - size + 1
      }
    }
    previous = current
  }
  return { a: bestLeft, b: bestRight, size: bestSize }
}

const getMatchingBlocks = (
  leftTokens: string[],
  rightTokens: string[],
  leftStart = 0,
  leftEnd = leftTokens.length,
  rightStart = 0,
  rightEnd = rightTokens.length
): ExactBlock[] => {
  const match = findLongestExactMatch(leftTokens, rightTokens, leftStart, leftEnd, rightStart, rightEnd)
  if (match.size === 0) {
    return []
  }
  const before = getMatchingBlocks(leftTokens, rightTokens, leftStart, match.a, rightStart, match.b)
  const after = getMatchingBlocks(
    leftTokens,
    rightTokens,
    match.a + match.size,
    leftEnd,
    match.b + match.size,
    rightEnd
  )
  return [...before, match, ...after]
}

const getMergedMatchingBlocks = (leftTokens: string[], rightTokens: string[]): ExactBlock[] => {
  const rawBlocks = [...getMatchingBlocks(leftTokens, rightTokens), { a: leftTokens.length, b: rightTokens.length, size: 0 }]
  const merged: ExactBlock[] = []
  rawBlocks.forEach(block => {
    const previous = merged[merged.length - 1]
    if (
      previous &&
      previous.a + previous.size === block.a &&
      previous.b + previous.size === block.b
    ) {
      previous.size += block.size
      return
    }
    merged.push({ ...block })
  })
  return merged
}

const findBestExactAnchor = (leftTokens: string[], rightTokens: string[]): ExactBlock | null => {
  const matchingBlocks = getMergedMatchingBlocks(leftTokens, rightTokens)
  const filtered = matchingBlocks.filter(candidate => {
    if (candidate.size === 0) {
      return false
    }
    if (candidate.size >= MIN_EXACT_ANCHOR) {
      // continue through general filters
    } else if (candidate.size === 2) {
      const contentCount = leftTokens.slice(candidate.a, candidate.a + candidate.size).filter(isContentToken).length
      if (contentCount < 1) {
        return false
      }
    } else if (candidate.size === 1) {
      const token = leftTokens[candidate.a]
      const isEdge = (
        candidate.a === 0 ||
        candidate.b === 0 ||
        candidate.a + candidate.size === leftTokens.length ||
        candidate.b + candidate.size === rightTokens.length
      )
      if (!isEdge || !isContentToken(token) || Math.max(leftTokens.length, rightTokens.length) > 5) {
        return false
      }
    } else {
      return false
    }
    const leftUnmatched = leftTokens.length - candidate.size
    const rightUnmatched = rightTokens.length - candidate.size
    if (Math.min(leftUnmatched, rightUnmatched) <= 1 && Math.max(leftUnmatched, rightUnmatched) >= 3) {
      return false
    }
    if (candidate.size === 1) {
      const leftIsEdge = candidate.a === 0 || candidate.a + candidate.size === leftTokens.length
      const rightIsEdge = candidate.b === 0 || candidate.b + candidate.size === rightTokens.length
      if ((leftIsEdge !== rightIsEdge || !(leftIsEdge && rightIsEdge)) && Math.max(leftUnmatched, rightUnmatched) >= 3) {
        return false
      }
    }
    return true
  })
  if (!filtered.length) {
    return null
  }
  return filtered
    .slice()
    .sort((left, right) => {
      const leftContent = leftTokens.slice(left.a, left.a + left.size).filter(isContentToken).length
      const rightContent = leftTokens.slice(right.a, right.a + right.size).filter(isContentToken).length
      const leftScore = left.size * 2 + leftContent
      const rightScore = right.size * 2 + rightContent
      if (rightScore !== leftScore) return rightScore - leftScore
      if (left.a !== right.a) return left.a - right.a
      return left.b - right.b
    })[0] || null
}

const resolveRecursiveBlocks = (
  leftTokens: string[],
  rightTokens: string[],
  leftOffset: number,
  rightOffset: number
): ApparatusSpan[] => {
  const anchor = findBestExactAnchor(leftTokens, rightTokens)
  if (!anchor) {
    return resolveTokens(leftTokens, rightTokens, leftOffset, rightOffset)
  }
  const spans: ApparatusSpan[] = []
  const beforeLeft = leftTokens.slice(0, anchor.a)
  const beforeRight = rightTokens.slice(0, anchor.b)
  if (beforeLeft.length || beforeRight.length) {
    spans.push(...resolveRecursiveBlocks(beforeLeft, beforeRight, leftOffset, rightOffset))
  }
  const afterLeft = leftTokens.slice(anchor.a + anchor.size)
  const afterRight = rightTokens.slice(anchor.b + anchor.size)
  if (afterLeft.length || afterRight.length) {
    spans.push(...resolveRecursiveBlocks(
      afterLeft,
      afterRight,
      leftOffset + anchor.a + anchor.size,
      rightOffset + anchor.b + anchor.size
    ))
  }
  return spans
}

type SequenceOpcode = {
  tag: 'equal' | 'insert' | 'delete' | 'replace'
  leftStart: number
  leftEnd: number
  rightStart: number
  rightEnd: number
}

const buildSequenceOpcodesForSequences = (leftSequence: string[], rightSequence: string[]): SequenceOpcode[] => {
  const blocks = getMergedMatchingBlocks(leftSequence, rightSequence)
  const opcodes: SequenceOpcode[] = []
  let leftIndex = 0
  let rightIndex = 0
  blocks.forEach(block => {
    const leftTag = leftIndex < block.a
    const rightTag = rightIndex < block.b
    let tag: SequenceOpcode['tag'] | null = null
    if (leftTag && rightTag) {
      tag = 'replace'
    } else if (leftTag) {
      tag = 'delete'
    } else if (rightTag) {
      tag = 'insert'
    }
    if (tag) {
      opcodes.push({
        tag,
        leftStart: leftIndex,
        leftEnd: block.a,
        rightStart: rightIndex,
        rightEnd: block.b
      })
    }
    if (block.size > 0) {
      opcodes.push({
        tag: 'equal',
        leftStart: block.a,
        leftEnd: block.a + block.size,
        rightStart: block.b,
        rightEnd: block.b + block.size
      })
    }
    leftIndex = block.a + block.size
    rightIndex = block.b + block.size
  })
  return opcodes
}

const buildSequenceOpcodes = (leftTokens: string[], rightTokens: string[]): SequenceOpcode[] => (
  buildSequenceOpcodesForSequences(leftTokens, rightTokens)
)

const buildGapAnalysis = (leftTokens: string[], rightTokens: string[], leftOffset: number, rightOffset: number): GapAnalysis => {
  const smallSpans: ApparatusSpan[] = []
  let onlySmallOneSided = true
  let hasLargeOneSidedTail = false
  buildSequenceOpcodes(leftTokens, rightTokens).forEach(opcode => {
    if (opcode.tag === 'equal') {
      return
    }
    const leftBlock = leftTokens.slice(opcode.leftStart, opcode.leftEnd)
    const rightBlock = rightTokens.slice(opcode.rightStart, opcode.rightEnd)
    if (opcode.tag === 'insert') {
      const rightText = joinTokens(rightBlock)
      if (rightBlock.length <= SMALL_GAP_LIMIT && rightText) {
        smallSpans.push({
          kind: 'insertion',
          leftText: '',
          rightText,
          leftStart: leftOffset + opcode.leftStart,
          leftEnd: leftOffset + opcode.leftStart,
          rightStart: rightOffset + opcode.rightStart,
          rightEnd: rightOffset + opcode.rightEnd
        })
      } else {
        hasLargeOneSidedTail = true
        onlySmallOneSided = false
      }
      return
    }
    if (opcode.tag === 'delete') {
      const leftText = joinTokens(leftBlock)
      if (leftBlock.length <= SMALL_GAP_LIMIT && leftText) {
        smallSpans.push({
          kind: 'deletion',
          leftText,
          rightText: '',
          leftStart: leftOffset + opcode.leftStart,
          leftEnd: leftOffset + opcode.leftEnd,
          rightStart: rightOffset + opcode.rightStart,
          rightEnd: rightOffset + opcode.rightStart
        })
      } else {
        hasLargeOneSidedTail = true
        onlySmallOneSided = false
      }
      return
    }
    onlySmallOneSided = false
  })
  return { smallSpans, onlySmallOneSided: onlySmallOneSided && smallSpans.length > 0, hasLargeOneSidedTail }
}

const splitInsertionTokens = (tokens: string[], leftOffset: number, rightOffset: number): ApparatusSpan[] => {
  if (!tokens.length) return []
  if (tokens.length <= 2) {
    return [{
      kind: 'insertion',
      leftText: '',
      rightText: joinTokens(tokens),
      leftStart: leftOffset,
      leftEnd: leftOffset,
      rightStart: rightOffset,
      rightEnd: rightOffset + tokens.length
    }]
  }
  if (isContentToken(tokens[0]) && tokens.slice(1).length >= 2) {
    return [
      {
        kind: 'insertion',
        leftText: '',
        rightText: tokens[0],
        leftStart: leftOffset,
        leftEnd: leftOffset,
        rightStart: rightOffset,
        rightEnd: rightOffset + 1
      },
      {
        kind: 'insertion',
        leftText: '',
        rightText: joinTokens(tokens.slice(1)),
        leftStart: leftOffset,
        leftEnd: leftOffset,
        rightStart: rightOffset + 1,
        rightEnd: rightOffset + tokens.length
      }
    ]
  }
  return [{
    kind: 'insertion',
    leftText: '',
    rightText: joinTokens(tokens),
    leftStart: leftOffset,
    leftEnd: leftOffset,
    rightStart: rightOffset,
    rightEnd: rightOffset + tokens.length
  }]
}

const splitDeletionTokens = (tokens: string[], leftOffset: number, rightOffset: number): ApparatusSpan[] => {
  if (!tokens.length) return []
  return [{
    kind: 'deletion',
    leftText: joinTokens(tokens),
    rightText: '',
    leftStart: leftOffset,
    leftEnd: leftOffset + tokens.length,
    rightStart: rightOffset,
    rightEnd: rightOffset
  }]
}

const countStrongMatchCandidates = (leftTokens: string[], rightTokens: string[]): number => {
  let count = 0
  for (const leftToken of leftTokens) {
    if (!isContentToken(leftToken)) continue
    for (const rightToken of rightTokens) {
      if (!isContentToken(rightToken)) continue
      const kind = classifyTokenPair(leftToken, rightToken, { allowLexical: false })
      if (kind === 'morph' || kind === 'vocalization' || kind === 'orthographic' || kind === 'grammatical_shift' || kind === 'synonym') {
        count += 1
      }
    }
  }
  return count
}

const bestStrongMatch = (leftTokens: string[], rightTokens: string[]): TokenMatch | null => {
  let best: TokenMatch | null = null
  const weights: Record<'morph' | 'vocalization' | 'orthographic' | 'grammatical_shift' | 'synonym', number> = {
    morph: 4,
    vocalization: 3.5,
    orthographic: 3,
    grammatical_shift: 2.75,
    synonym: 2
  }
  for (let leftIndex = 0; leftIndex < leftTokens.length; leftIndex += 1) {
    if (!isContentToken(leftTokens[leftIndex])) continue
    for (let rightIndex = 0; rightIndex < rightTokens.length; rightIndex += 1) {
      if (!isContentToken(rightTokens[rightIndex])) continue
      const kind = classifyTokenPair(leftTokens[leftIndex], rightTokens[rightIndex], { allowLexical: false })
      if (kind !== 'morph' && kind !== 'vocalization' && kind !== 'orthographic' && kind !== 'grammatical_shift' && kind !== 'synonym') continue
      const score = weights[kind] - Math.abs(leftIndex - rightIndex) * 0.05
      if (!best || score > best.score) {
        best = { leftIndex, rightIndex, kind, score }
      }
    }
  }
  return best
}

const bestLexicalBoundaryMatch = (leftTokens: string[], rightTokens: string[]): TokenMatch | null => {
  if (!leftTokens.length || !rightTokens.length) return null
  const candidatePositions: Array<[number, number]> = [
    [0, 0],
    [leftTokens.length - 1, rightTokens.length - 1]
  ]
  let best: TokenMatch | null = null
  for (const [leftIndex, rightIndex] of candidatePositions) {
    const kind = classifyTokenPair(leftTokens[leftIndex], rightTokens[rightIndex], { allowLexical: true })
    if (kind !== 'lexical_substitution') continue
    const edgeBonus = (leftIndex === leftTokens.length - 1 && rightIndex === rightTokens.length - 1) ? 0.3 : 0
    const score = 1.5 + edgeBonus - Math.abs(leftIndex - rightIndex) * 0.05
    if (!best || score > best.score) {
      best = { leftIndex, rightIndex, kind, score }
    }
  }
  return best
}

const maybeResolveScopeShiftZone = (
  leftTokens: string[],
  rightTokens: string[],
  leftOffset: number,
  rightOffset: number
): ApparatusSpan[] | null => {
  const opcodes = buildSequenceOpcodes(leftTokens, rightTokens)
  if (opcodes.length < 3) {
    return null
  }
  const first = opcodes[0]
  const last = opcodes[opcodes.length - 1]
  if (first.tag === 'equal' || last.tag === 'equal') {
    return null
  }
  const priorEqualOps = opcodes.slice(0, -1).filter(opcode => opcode.tag === 'equal')
  if (!priorEqualOps.length) {
    return null
  }
  const longestEqualLength = Math.max(...priorEqualOps.map(opcode => opcode.leftEnd - opcode.leftStart))
  if (longestEqualLength > 2) {
    return null
  }

  if (last.tag === 'delete') {
    const tailLength = last.leftEnd - last.leftStart
    const blockLeftEnd = last.leftStart
    const blockLeftTokens = leftTokens.slice(0, blockLeftEnd)
    const blockRightTokens = rightTokens.slice(0, last.rightStart)
    const spans: ApparatusSpan[] = []
    if (blockLeftTokens.length || blockRightTokens.length) {
      spans.push({
        kind: 'block_substitution',
        leftText: joinTokens(blockLeftTokens),
        rightText: joinTokens(blockRightTokens),
        leftStart: leftOffset,
        leftEnd: leftOffset + blockLeftTokens.length,
        rightStart: rightOffset,
        rightEnd: rightOffset + blockRightTokens.length
      })
    }
    if (tailLength > 0) {
      const deletionTokens = leftTokens.slice(last.leftStart, last.leftEnd)
      spans.push({
        kind: 'deletion',
        leftText: joinTokens(deletionTokens),
        rightText: '',
        leftStart: leftOffset + last.leftStart,
        leftEnd: leftOffset + last.leftEnd,
        rightStart: rightOffset + last.rightStart,
        rightEnd: rightOffset + last.rightStart
      })
    }
    return spans
  }

  if (last.tag === 'insert') {
    const tailLength = last.rightEnd - last.rightStart
    const blockLeftTokens = leftTokens.slice(0, last.leftStart)
    const blockRightTokens = rightTokens.slice(0, last.rightStart)
    const spans: ApparatusSpan[] = []
    if (blockLeftTokens.length || blockRightTokens.length) {
      spans.push({
        kind: 'block_substitution',
        leftText: joinTokens(blockLeftTokens),
        rightText: joinTokens(blockRightTokens),
        leftStart: leftOffset,
        leftEnd: leftOffset + blockLeftTokens.length,
        rightStart: rightOffset,
        rightEnd: rightOffset + blockRightTokens.length
      })
    }
    if (tailLength > 0) {
      const insertionTokens = rightTokens.slice(last.rightStart, last.rightEnd)
      spans.push({
        kind: 'insertion',
        leftText: '',
        rightText: joinTokens(insertionTokens),
        leftStart: leftOffset + last.leftStart,
        leftEnd: leftOffset + last.leftStart,
        rightStart: rightOffset + last.rightStart,
        rightEnd: rightOffset + last.rightEnd
      })
    }
    return spans
  }

  return null
}

const bestSingleToManyMatch = (token: string, otherTokens: string[]): [number, ApparatusVariantKind] | null => {
  let best: [number, number, ApparatusVariantKind] | null = null
  for (let index = 0; index < otherTokens.length; index += 1) {
    const kind = classifyTokenPair(token, otherTokens[index], { allowLexical: true })
    if (!kind || kind === 'lexical_substitution') continue
    const baseScore = {
      morph: 4,
      vocalization: 3.5,
      orthographic: 3,
      grammatical_shift: 2.75,
      synonym: 2.5,
      reordering: 0,
      insertion: 0,
      deletion: 0,
      block_substitution: 0
    }[kind]
    const score = baseScore
    if (!best || score > best[0]) {
      best = [score, index, kind]
    }
  }
  return best ? [best[1], best[2]] : null
}

const resolveAlignedSmallZone = (
  leftTokens: string[],
  rightTokens: string[],
  leftOffset: number,
  rightOffset: number
): ApparatusSpan[] | null => {
  if (!leftTokens.length || !rightTokens.length || leftTokens.length !== rightTokens.length || leftTokens.length > 3) {
    return null
  }
  const spans: ApparatusSpan[] = []
  for (let index = 0; index < leftTokens.length; index += 1) {
    const kind = classifyTokenPair(leftTokens[index], rightTokens[index], { allowLexical: true })
    if (!kind) {
      return null
    }
    spans.push({
      kind,
      leftText: leftTokens[index],
      rightText: rightTokens[index],
      leftStart: leftOffset + index,
      leftEnd: leftOffset + index + 1,
      rightStart: rightOffset + index,
      rightEnd: rightOffset + index + 1
    })
  }
  return spans
}

const resolveProhibitiveConstruction = (
  leftTokens: string[],
  rightTokens: string[],
  leftOffset: number,
  rightOffset: number
): ApparatusSpan[] | null => {
  const buildLeftPronounPattern = (): ApparatusSpan[] | null => {
    if (leftTokens.length < 3 || rightTokens.length < 2) return null
    if (!isDetachedPronounToken(leftTokens[0]) || !isAnToken(leftTokens[1]) || !isNegationToken(rightTokens[0])) {
      return null
    }
    const verbKind = classifyTokenPair(leftTokens[2], rightTokens[1], { allowLexical: true })
    if (!verbKind || verbKind === 'block_substitution' || verbKind === 'deletion' || verbKind === 'insertion') {
      return null
    }
    return [
      {
        kind: 'grammatical_shift',
        leftText: leftTokens[0],
        rightText: rightTokens[0],
        leftStart: leftOffset,
        leftEnd: leftOffset + 1,
        rightStart: rightOffset,
        rightEnd: rightOffset + 1
      },
      {
        kind: 'deletion',
        leftText: leftTokens[1],
        rightText: '',
        leftStart: leftOffset + 1,
        leftEnd: leftOffset + 2,
        rightStart: rightOffset + 1,
        rightEnd: rightOffset + 1
      },
      {
        kind: verbKind,
        leftText: leftTokens[2],
        rightText: rightTokens[1],
        leftStart: leftOffset + 2,
        leftEnd: leftOffset + 3,
        rightStart: rightOffset + 1,
        rightEnd: rightOffset + 2
      },
      ...resolveTokens(leftTokens.slice(3), rightTokens.slice(2), leftOffset + 3, rightOffset + 2)
    ]
  }

  const buildRightPronounPattern = (): ApparatusSpan[] | null => {
    if (rightTokens.length < 3 || leftTokens.length < 2) return null
    if (!isDetachedPronounToken(rightTokens[0]) || !isAnToken(rightTokens[1]) || !isNegationToken(leftTokens[0])) {
      return null
    }
    const verbKind = classifyTokenPair(leftTokens[1], rightTokens[2], { allowLexical: true })
    if (!verbKind || verbKind === 'block_substitution' || verbKind === 'deletion' || verbKind === 'insertion') {
      return null
    }
    return [
      {
        kind: 'grammatical_shift',
        leftText: leftTokens[0],
        rightText: rightTokens[0],
        leftStart: leftOffset,
        leftEnd: leftOffset + 1,
        rightStart: rightOffset,
        rightEnd: rightOffset + 1
      },
      {
        kind: 'insertion',
        leftText: '',
        rightText: rightTokens[1],
        leftStart: leftOffset + 1,
        leftEnd: leftOffset + 1,
        rightStart: rightOffset + 1,
        rightEnd: rightOffset + 2
      },
      {
        kind: verbKind,
        leftText: leftTokens[1],
        rightText: rightTokens[2],
        leftStart: leftOffset + 1,
        leftEnd: leftOffset + 2,
        rightStart: rightOffset + 2,
        rightEnd: rightOffset + 3
      },
      ...resolveTokens(leftTokens.slice(2), rightTokens.slice(3), leftOffset + 2, rightOffset + 3)
    ]
  }

  return buildLeftPronounPattern() || buildRightPronounPattern()
}

const isStructuredCompactResolution = (
  spans: ApparatusSpan[],
  leftText: string,
  rightText: string
): boolean => {
  if (!spans.length) return false
  return spans.some(span => span.kind !== 'deletion' && span.kind !== 'insertion' && span.kind !== 'block_substitution')
    && !spans.some(span => span.kind === 'block_substitution' && span.leftText === leftText && span.rightText === rightText)
}

const scoreCompactRewriteResolution = (spans: ApparatusSpan[], prefixLength: number): number => {
  const articulatedCount = spans.filter(span => span.kind !== 'deletion' && span.kind !== 'insertion' && span.kind !== 'block_substitution').length
  const blockCount = spans.filter(span => span.kind === 'block_substitution').length
  return articulatedCount * 10 - blockCount * 5 + prefixLength
}

const resolveAsymmetricCompactRewrite = (
  leftTokens: string[],
  rightTokens: string[],
  leftOffset: number,
  rightOffset: number
): ApparatusSpan[] | null => {
  if (leftTokens.length >= 5 && leftTokens.length - rightTokens.length >= 3 && rightTokens.length >= 2 && rightTokens.length <= 3) {
    let best: { score: number; spans: ApparatusSpan[] } | null = null
    for (let suffixSize = Math.min(4, leftTokens.length - 1); suffixSize >= 2; suffixSize -= 1) {
      const prefix = leftTokens.slice(0, leftTokens.length - suffixSize)
      const suffix = leftTokens.slice(leftTokens.length - suffixSize)
      const suffixSpans = resolveTokens(suffix, rightTokens, leftOffset + prefix.length, rightOffset)
      if (!isStructuredCompactResolution(suffixSpans, joinTokens(suffix), joinTokens(rightTokens))) {
        continue
      }
      const candidateSpans = [
        ...splitDeletionTokens(prefix, leftOffset, rightOffset),
        ...suffixSpans
      ]
      const score = scoreCompactRewriteResolution(suffixSpans, prefix.length)
      if (!best || score > best.score) {
        best = { score, spans: candidateSpans }
      }
    }
    return best?.spans || null
  }

  return null
}

const allNonContent = (tokens: string[]): boolean => tokens.length > 0 && tokens.every(token => !isContentToken(token))

const stripEdgeExactMatches = (
  leftTokens: string[],
  rightTokens: string[]
): { left: string[]; right: string[]; leftLeadingTrim: number; rightLeadingTrim: number } => {
  const left = [...leftTokens]
  const right = [...rightTokens]
  let leftLeadingTrim = 0
  let rightLeadingTrim = 0
  while (left.length && right.length && normalizeApparatusToken(left[0]) === normalizeApparatusToken(right[0])) {
    left.shift()
    right.shift()
    leftLeadingTrim += 1
    rightLeadingTrim += 1
  }
  while (left.length && right.length && normalizeApparatusToken(left[left.length - 1]) === normalizeApparatusToken(right[right.length - 1])) {
    left.pop()
    right.pop()
  }
  return { left, right, leftLeadingTrim, rightLeadingTrim }
}

const resolveTokens = (leftTokens: string[], rightTokens: string[], leftOffset: number, rightOffset: number): ApparatusSpan[] => {
  if (!leftTokens.length && !rightTokens.length) return []
  if (!leftTokens.length) return splitInsertionTokens(rightTokens, leftOffset, rightOffset)
  if (!rightTokens.length) return splitDeletionTokens(leftTokens, leftOffset, rightOffset)

  if (leftTokens.length === 1 && rightTokens.length === 1) {
    const kind = classifyTokenPair(leftTokens[0], rightTokens[0], { allowLexical: true })
    return [kind ? {
      kind,
      leftText: leftTokens[0],
      rightText: rightTokens[0],
      leftStart: leftOffset,
      leftEnd: leftOffset + 1,
      rightStart: rightOffset,
      rightEnd: rightOffset + 1
    } : {
      kind: 'block_substitution',
      leftText: leftTokens[0],
      rightText: rightTokens[0],
      leftStart: leftOffset,
      leftEnd: leftOffset + 1,
      rightStart: rightOffset,
      rightEnd: rightOffset + 1
    }]
  }

  if (allNonContent(leftTokens) && !allNonContent(rightTokens)) return splitInsertionTokens(rightTokens, leftOffset, rightOffset)
  if (allNonContent(rightTokens) && !allNonContent(leftTokens)) return splitDeletionTokens(leftTokens, leftOffset, rightOffset)

  const stripped = stripEdgeExactMatches(leftTokens, rightTokens)
  const strippedLeft = stripped.left
  const strippedRight = stripped.right
  const strippedLeftOffset = leftOffset + stripped.leftLeadingTrim
  const strippedRightOffset = rightOffset + stripped.rightLeadingTrim
  if (!strippedLeft.length && !strippedRight.length) return []
  if (!strippedLeft.length) return splitInsertionTokens(strippedRight, strippedLeftOffset, strippedRightOffset)
  if (!strippedRight.length) return splitDeletionTokens(strippedLeft, strippedLeftOffset, strippedRightOffset)

  const prohibitiveSpans = resolveProhibitiveConstruction(strippedLeft, strippedRight, strippedLeftOffset, strippedRightOffset)
  if (prohibitiveSpans) {
    return prohibitiveSpans
  }

  const gapAnalysis = buildGapAnalysis(strippedLeft, strippedRight, strippedLeftOffset, strippedRightOffset)
  if (gapAnalysis.onlySmallOneSided) return gapAnalysis.smallSpans

  const strongCandidateCount = countStrongMatchCandidates(strippedLeft, strippedRight)
  if (gapAnalysis.hasLargeOneSidedTail && Math.max(strippedLeft.length, strippedRight.length) >= 2 * Math.max(1, Math.min(strippedLeft.length, strippedRight.length))) {
    return [...gapAnalysis.smallSpans, {
      kind: 'block_substitution',
      leftText: joinTokens(strippedLeft),
      rightText: joinTokens(strippedRight),
      leftStart: strippedLeftOffset,
      leftEnd: strippedLeftOffset + strippedLeft.length,
      rightStart: strippedRightOffset,
      rightEnd: strippedRightOffset + strippedRight.length
    }]
  }

  if (strippedLeft.length === 1 && strippedRight.length > 1) {
    const candidate = bestSingleToManyMatch(strippedLeft[0], strippedRight)
    if (candidate) {
      const [rightIndex, kind] = candidate
      if (rightIndex === 0) {
        return [{
          kind,
          leftText: strippedLeft[0],
          rightText: strippedRight[0],
          leftStart: strippedLeftOffset,
          leftEnd: strippedLeftOffset + 1,
          rightStart: strippedRightOffset,
          rightEnd: strippedRightOffset + 1
        }, ...resolveTokens([], strippedRight.slice(1), strippedLeftOffset + 1, strippedRightOffset + 1)]
      }
      return [
        ...resolveTokens([], strippedRight.slice(0, rightIndex), strippedLeftOffset, strippedRightOffset),
        {
          kind,
          leftText: strippedLeft[0],
          rightText: strippedRight[rightIndex],
          leftStart: strippedLeftOffset,
          leftEnd: strippedLeftOffset + 1,
          rightStart: strippedRightOffset + rightIndex,
          rightEnd: strippedRightOffset + rightIndex + 1
        },
        ...resolveTokens([], strippedRight.slice(rightIndex + 1), strippedLeftOffset + 1, strippedRightOffset + rightIndex + 1)
      ]
    }
  }

  if (strippedRight.length === 1 && strippedLeft.length > 1) {
    const candidate = bestSingleToManyMatch(strippedRight[0], strippedLeft)
    if (candidate) {
      const [leftIndex, kind] = candidate
      return [
        ...resolveTokens(strippedLeft.slice(0, leftIndex), [], strippedLeftOffset, strippedRightOffset),
        {
          kind,
          leftText: strippedLeft[leftIndex],
          rightText: strippedRight[0],
          leftStart: strippedLeftOffset + leftIndex,
          leftEnd: strippedLeftOffset + leftIndex + 1,
          rightStart: strippedRightOffset,
          rightEnd: strippedRightOffset + 1
        },
        ...resolveTokens(strippedLeft.slice(leftIndex + 1), [], strippedLeftOffset + leftIndex + 1, strippedRightOffset + 1)
      ]
    }
  }

  const strongMatch = bestStrongMatch(strippedLeft, strippedRight)
  if (
    strongMatch &&
    strongMatch.kind !== 'orthographic' &&
    strippedLeft.length >= 6 &&
    strippedRight.length >= 6 &&
    strongCandidateCount === 0
  ) {
    return [{
      kind: 'block_substitution',
      leftText: joinTokens(strippedLeft),
      rightText: joinTokens(strippedRight),
      leftStart: strippedLeftOffset,
      leftEnd: strippedLeftOffset + strippedLeft.length,
      rightStart: strippedRightOffset,
      rightEnd: strippedRightOffset + strippedRight.length
    }]
  }
  if (strongMatch) {
    return [
      ...resolveTokens(strippedLeft.slice(0, strongMatch.leftIndex), strippedRight.slice(0, strongMatch.rightIndex), strippedLeftOffset, strippedRightOffset),
      {
        kind: strongMatch.kind,
        leftText: strippedLeft[strongMatch.leftIndex],
        rightText: strippedRight[strongMatch.rightIndex],
        leftStart: strippedLeftOffset + strongMatch.leftIndex,
        leftEnd: strippedLeftOffset + strongMatch.leftIndex + 1,
        rightStart: strippedRightOffset + strongMatch.rightIndex,
        rightEnd: strippedRightOffset + strongMatch.rightIndex + 1
      },
      ...resolveTokens(strippedLeft.slice(strongMatch.leftIndex + 1), strippedRight.slice(strongMatch.rightIndex + 1), strippedLeftOffset + strongMatch.leftIndex + 1, strippedRightOffset + strongMatch.rightIndex + 1)
    ]
  }

  const scopeShiftSpans = maybeResolveScopeShiftZone(strippedLeft, strippedRight, strippedLeftOffset, strippedRightOffset)
  if (scopeShiftSpans) {
    return scopeShiftSpans
  }

  const innerAnchor = findBestExactAnchor(strippedLeft, strippedRight)
  if (innerAnchor) {
    return resolveRecursiveBlocks(strippedLeft, strippedRight, strippedLeftOffset, strippedRightOffset)
  }

  const alignedSmallZone = resolveAlignedSmallZone(strippedLeft, strippedRight, strippedLeftOffset, strippedRightOffset)
  if (alignedSmallZone) {
    return alignedSmallZone
  }

  if (strippedLeft.length >= 3 && strippedRight.length >= 3) {
    return [{
      kind: 'block_substitution',
      leftText: joinTokens(strippedLeft),
      rightText: joinTokens(strippedRight),
      leftStart: strippedLeftOffset,
      leftEnd: strippedLeftOffset + strippedLeft.length,
      rightStart: strippedRightOffset,
      rightEnd: strippedRightOffset + strippedRight.length
    }]
  }

  const lexicalMatch = bestLexicalBoundaryMatch(strippedLeft, strippedRight)
  if (lexicalMatch) {
    return [
      ...resolveTokens(strippedLeft.slice(0, lexicalMatch.leftIndex), strippedRight.slice(0, lexicalMatch.rightIndex), strippedLeftOffset, strippedRightOffset),
      {
        kind: 'lexical_substitution',
        leftText: strippedLeft[lexicalMatch.leftIndex],
        rightText: strippedRight[lexicalMatch.rightIndex],
        leftStart: strippedLeftOffset + lexicalMatch.leftIndex,
        leftEnd: strippedLeftOffset + lexicalMatch.leftIndex + 1,
        rightStart: strippedRightOffset + lexicalMatch.rightIndex,
        rightEnd: strippedRightOffset + lexicalMatch.rightIndex + 1
      },
      ...resolveTokens(strippedLeft.slice(lexicalMatch.leftIndex + 1), strippedRight.slice(lexicalMatch.rightIndex + 1), strippedLeftOffset + lexicalMatch.leftIndex + 1, strippedRightOffset + lexicalMatch.rightIndex + 1)
    ]
  }

  if (gapAnalysis.hasLargeOneSidedTail) {
    return [...gapAnalysis.smallSpans, {
      kind: 'block_substitution',
      leftText: joinTokens(strippedLeft),
      rightText: joinTokens(strippedRight),
      leftStart: strippedLeftOffset,
      leftEnd: strippedLeftOffset + strippedLeft.length,
      rightStart: strippedRightOffset,
      rightEnd: strippedRightOffset + strippedRight.length
    }]
  }
  return [{
    kind: 'block_substitution',
    leftText: joinTokens(strippedLeft),
    rightText: joinTokens(strippedRight),
    leftStart: strippedLeftOffset,
    leftEnd: strippedLeftOffset + strippedLeft.length,
    rightStart: strippedRightOffset,
    rightEnd: strippedRightOffset + strippedRight.length
  }]
}

const isSingleTokenText = (text: string): boolean => tokenize(text).length === 1
const firstToken = (text: string): string => tokenize(text)[0] || ''
const restAfterFirstToken = (text: string): string => joinTokens(tokenize(text).slice(1))

const mergeDislocatedPairs = (spans: ApparatusSpan[]): ApparatusSpan[] => {
  const merged: ApparatusSpan[] = []
  let index = 0
  while (index < spans.length) {
    const current = spans[index]
    if ((current.kind === 'deletion' || current.kind === 'insertion') && isSingleTokenText(current.leftText || current.rightText)) {
      const currentToken = current.leftText || current.rightText
      const currentIsLeft = current.kind === 'deletion'
      let matchedIndex = -1
      let matchedKind: ApparatusVariantKind | null = null
      for (let candidateIndex = index + 1; candidateIndex < Math.min(index + 5, spans.length); candidateIndex += 1) {
        const candidate = spans[candidateIndex]
        if (currentIsLeft && candidate.kind !== 'insertion') continue
        if (!currentIsLeft && candidate.kind !== 'deletion') continue
        const candidateToken = currentIsLeft ? candidate.rightText : candidate.leftText
        if (!isSingleTokenText(candidateToken)) continue
      const kind = classifyTokenPair(currentIsLeft ? currentToken : candidateToken, currentIsLeft ? candidateToken : currentToken, { allowLexical: false })
        if (kind === 'synonym' || kind === 'morph' || kind === 'vocalization' || kind === 'orthographic' || kind === 'grammatical_shift') {
          matchedIndex = candidateIndex
          matchedKind = kind
          break
        }
      }
      if (matchedIndex >= 0 && matchedKind) {
        const candidate = spans[matchedIndex]
        merged.push({
          kind: matchedKind,
          leftText: current.leftText || candidate.leftText,
          rightText: current.rightText || candidate.rightText,
          note: 'dislocated',
          leftStart: current.leftStart ?? candidate.leftStart,
          leftEnd: current.leftEnd ?? candidate.leftEnd,
          rightStart: current.rightStart ?? candidate.rightStart,
          rightEnd: current.rightEnd ?? candidate.rightEnd
        })
        index = matchedIndex + 1
        continue
      }
    }
    merged.push(current)
    index += 1
  }
  return merged
}

const refineBlockEdges = (spans: ApparatusSpan[]): ApparatusSpan[] => {
  const refined: ApparatusSpan[] = []
  let index = 0
  while (index < spans.length) {
    const current = spans[index]
    if (current.kind === 'deletion' && isSingleTokenText(current.leftText) && index + 1 < spans.length) {
      const nextSpan = spans[index + 1]
      if (nextSpan.kind === 'block_substitution') {
        const rightFirst = firstToken(nextSpan.rightText)
        const rightRest = restAfterFirstToken(nextSpan.rightText)
        const strongKind = classifyTokenPair(current.leftText, rightFirst, { allowLexical: false })
        if (strongKind === 'synonym' || strongKind === 'morph' || strongKind === 'vocalization' || strongKind === 'orthographic' || strongKind === 'grammatical_shift') {
          refined.push({
            kind: strongKind,
            leftText: current.leftText,
            rightText: rightFirst,
            note: 'block-edge',
            leftStart: current.leftStart,
            leftEnd: current.leftEnd,
            rightStart: nextSpan.rightStart,
            rightEnd: nextSpan.rightStart !== undefined ? nextSpan.rightStart + 1 : undefined
          })
          let nextLeft = nextSpan.leftText
          let nextLeftEnd = nextSpan.leftEnd
          if (index + 2 < spans.length && spans[index + 2]?.kind === 'deletion') {
            nextLeft = joinTokens([nextLeft, spans[index + 2]?.leftText || ''])
            nextLeftEnd = spans[index + 2]?.leftEnd ?? nextLeftEnd
            index += 1
          }
          if (nextLeft || rightRest) {
            refined.push({
              kind: 'block_substitution',
              leftText: nextLeft,
              rightText: rightRest,
              leftStart: nextSpan.leftStart,
              leftEnd: nextLeftEnd,
              rightStart: nextSpan.rightStart !== undefined ? nextSpan.rightStart + 1 : undefined,
              rightEnd: nextSpan.rightEnd
            })
          }
          index += 2
          continue
        }
      }
    }
    refined.push(current)
    index += 1
  }
  return refined
}

const injectLongRangeSynonymLinks = (spans: ApparatusSpan[]): ApparatusSpan[] => {
  const augmented = [...spans]
  const seenPairs = new Set(spans.map(span => `${span.kind}|${span.leftText}|${span.rightText}`))
  const leftCandidates: Array<[number, string, ApparatusSpan]> = []
  spans.forEach((span, index) => {
    if (span.kind === 'deletion' && isSingleTokenText(span.leftText)) {
      leftCandidates.push([index, span.leftText, span])
    } else if (span.kind === 'block_substitution') {
      const left = firstToken(span.leftText)
      if (left) {
        leftCandidates.push([index, left, span])
      }
    }
  })
  for (let index = 0; index < spans.length; index += 1) {
    const span = spans[index]
    if (span.kind !== 'insertion' && span.kind !== 'block_substitution') continue
    const right = firstToken(span.rightText)
    if (!right) continue
    for (const [leftIndex, leftToken, leftSpan] of leftCandidates) {
      if (leftIndex >= index) continue
      const kind = classifyTokenPair(leftToken, right, { allowLexical: false })
      if (kind !== 'synonym') continue
      const key = `synonym|${leftToken}|${right}`
      if (seenPairs.has(key)) break
      augmented.splice(index, 0, {
        kind: 'synonym',
        leftText: leftToken,
        rightText: right,
        note: 'long-range',
        leftStart: leftSpan.leftStart,
        leftEnd: leftSpan.leftStart !== undefined ? leftSpan.leftStart + 1 : undefined,
        rightStart: span.rightStart,
        rightEnd: span.rightStart !== undefined ? span.rightStart + 1 : undefined
      })
      seenPairs.add(key)
      break
    }
  }
  return augmented
}

const classifyAlignedEqualPair = (left: string, right: string): ApparatusVariantKind | null => {
  if (left === right) {
    return null
  }
  if (isVocalizationMatch(left, right)) {
    return 'vocalization'
  }
  if (isOrthographicMatch(left, right)) {
    return 'orthographic'
  }
  return classifyTokenPair(left, right, { allowLexical: true })
}

const resolveLongReplaceBlock = (
  leftBlock: string[],
  rightBlock: string[],
  leftOffset: number,
  rightOffset: number
): ApparatusSpan[] => {
  if (!leftBlock.length && !rightBlock.length) return []
  if (!leftBlock.length) return splitInsertionTokens(rightBlock, leftOffset, rightOffset)
  if (!rightBlock.length) return splitDeletionTokens(leftBlock, leftOffset, rightOffset)
  if (leftBlock.length === 1 && rightBlock.length === 1) {
    const kind = classifyTokenPair(leftBlock[0], rightBlock[0], { allowLexical: true })
    return [kind ? {
      kind,
      leftText: leftBlock[0],
      rightText: rightBlock[0],
      leftStart: leftOffset,
      leftEnd: leftOffset + 1,
      rightStart: rightOffset,
      rightEnd: rightOffset + 1
    } : {
      kind: 'block_substitution',
      leftText: leftBlock[0],
      rightText: rightBlock[0],
      leftStart: leftOffset,
      leftEnd: leftOffset + 1,
      rightStart: rightOffset,
      rightEnd: rightOffset + 1
    }]
  }

  if (findBestExactAnchor(leftBlock, rightBlock)) {
    return resolveRecursiveBlocks(leftBlock, rightBlock, leftOffset, rightOffset)
  }

  const asymmetricCompactRewrite = resolveAsymmetricCompactRewrite(leftBlock, rightBlock, leftOffset, rightOffset)
  if (asymmetricCompactRewrite) {
    return asymmetricCompactRewrite
  }

  const strongCandidateCount = countStrongMatchCandidates(leftBlock, rightBlock)
  if (Math.max(leftBlock.length, rightBlock.length) >= 8) {
    return [{
      kind: 'block_substitution',
      leftText: joinTokens(leftBlock),
      rightText: joinTokens(rightBlock),
      leftStart: leftOffset,
      leftEnd: leftOffset + leftBlock.length,
      rightStart: rightOffset,
      rightEnd: rightOffset + rightBlock.length
    }]
  }
  if (strongCandidateCount === 0 && leftBlock.length + rightBlock.length >= 4) {
    return [{
      kind: 'block_substitution',
      leftText: joinTokens(leftBlock),
      rightText: joinTokens(rightBlock),
      leftStart: leftOffset,
      leftEnd: leftOffset + leftBlock.length,
      rightStart: rightOffset,
      rightEnd: rightOffset + rightBlock.length
    }]
  }
  return resolveTokens(leftBlock, rightBlock, leftOffset, rightOffset)
}

const buildLongModeSpans = (leftTokens: string[], rightTokens: string[]): ApparatusSpan[] => {
  const normalizedLeft = leftTokens.map(token => normalizeApparatusToken(token))
  const normalizedRight = rightTokens.map(token => normalizeApparatusToken(token))
  const spans: ApparatusSpan[] = []

  buildSequenceOpcodesForSequences(normalizedLeft, normalizedRight).forEach(opcode => {
    const leftBlock = leftTokens.slice(opcode.leftStart, opcode.leftEnd)
    const rightBlock = rightTokens.slice(opcode.rightStart, opcode.rightEnd)
    if (opcode.tag === 'equal') {
      leftBlock.forEach((leftToken, index) => {
        const rightToken = rightBlock[index]
        const kind = classifyAlignedEqualPair(leftToken, rightToken)
        if (!kind) {
          return
        }
        spans.push({
          kind,
          leftText: leftToken,
          rightText: rightToken,
          leftStart: opcode.leftStart + index,
          leftEnd: opcode.leftStart + index + 1,
          rightStart: opcode.rightStart + index,
          rightEnd: opcode.rightStart + index + 1
        })
      })
      return
    }
    if (opcode.tag === 'delete') {
      spans.push(...splitDeletionTokens(leftBlock, opcode.leftStart, opcode.rightStart))
      return
    }
    if (opcode.tag === 'insert') {
      spans.push(...splitInsertionTokens(rightBlock, opcode.leftStart, opcode.rightStart))
      return
    }
    spans.push(...resolveLongReplaceBlock(leftBlock, rightBlock, opcode.leftStart, opcode.rightStart))
  })

  return spans
}

export const buildApparatusSpans = (leftText: string, rightText: string): ApparatusSpan[] => {
  const leftTokens = tokenize(leftText)
  const rightTokens = tokenize(rightText)
  const spans = Math.max(leftTokens.length, rightTokens.length) >= LONG_DIFF_TOKEN_THRESHOLD
    ? buildLongModeSpans(leftTokens, rightTokens)
    : resolveRecursiveBlocks(leftTokens, rightTokens, 0, 0)
  const filtered = spans.filter(span => Boolean(span.leftText || span.rightText))
  const refined = injectLongRangeSynonymLinks(refineBlockEdges(mergeDislocatedPairs(filtered)))
  return appendReorderingSpans(refined, leftTokens, rightTokens)
}

export const buildApparatusOverlayTokens = (
  leftText: string,
  rightText: string,
  spans: ApparatusSpan[],
  side: 'left' | 'right'
): ApparatusOverlayToken[] => {
  const cleanedLeftText = cleanTextForComparison(leftText)
  const cleanedRightText = cleanTextForComparison(rightText)
  const cleanedText = side === 'left' ? cleanedLeftText : cleanedRightText
  const parts = tokenizeWithSpaces(cleanedText)
  const wordParts = parts.filter(part => !part.isSpace)
  const labels: Array<ApparatusVariantKind | undefined> = new Array(wordParts.length).fill(undefined)
  const ghostsByWordIndex = new Map<number, string[]>()
  spans.forEach(span => {
    const spanStart = side === 'left' ? span.leftStart : span.rightStart
    const spanEnd = side === 'left' ? span.leftEnd : span.rightEnd
    if (spanStart === undefined || spanEnd === undefined) {
      return
    }
    if (spanEnd > spanStart) {
      for (let index = Math.max(0, spanStart); index < Math.min(spanEnd, wordParts.length); index += 1) {
        labels[index] = span.kind
      }
      return
    }
    const ghostText = side === 'right'
      ? (span.kind === 'deletion' ? span.leftText : '')
      : (span.kind === 'insertion' ? span.rightText : '')
    if (!ghostText.trim()) {
      return
    }
    const resolvedGhostIndex = Math.min(Math.max(0, spanStart), wordParts.length)
    const existing = ghostsByWordIndex.get(resolvedGhostIndex) || []
    existing.push(ghostText)
    ghostsByWordIndex.set(resolvedGhostIndex, existing)
  })

  let wordIndex = 0
  const overlayTokens: ApparatusOverlayToken[] = []
  const pushGhosts = (index: number) => {
    const ghosts = ghostsByWordIndex.get(index) || []
    ghosts.forEach((ghostText, ghostIndex) => {
      if (overlayTokens.length > 0 && overlayTokens[overlayTokens.length - 1]?.kind !== 'space') {
        overlayTokens.push({ kind: 'space', text: ' ' })
      }
      overlayTokens.push({
        kind: 'ghost',
        text: ghostText,
        variant: 'deletion'
      })
      if (ghostIndex < ghosts.length - 1 || index < wordParts.length) {
        overlayTokens.push({ kind: 'space', text: ' ' })
      }
    })
  }

  parts.forEach(part => {
    if (part.isSpace) {
      overlayTokens.push({ kind: 'space', text: part.text })
      return
    }
    pushGhosts(wordIndex)
    const variant = labels[wordIndex]
    wordIndex += 1
    overlayTokens.push({
      kind: 'word' as const,
      text: part.text,
      variant
    })
  })
  pushGhosts(wordIndex)
  return overlayTokens
}
